import { RSAA } from 'redux-api-middleware';
import getSourceOfStreamTypesURL from 'url-constructors/getSourceOfStreamTypesURL';
import { RootState } from 'redux/rootReducer';
import { Dispatch } from 'redux';
import { TGetRootState } from '../../types/shared';

const REQUEST = 'opus/sourceOfStreamTypes/REQUEST';
const SUCCESS = 'opus/sourceOfStreamTypes/SUCCESS';
const FAILURE = 'opus/sourceOfStreamTypes/FAILURE';

type SourceOfStreamActionTypes =
  | typeof REQUEST
  | typeof SUCCESS
  | typeof FAILURE;

interface ISourceOfStreamTypesAction {
  type: SourceOfStreamActionTypes;
  payload: any;
}

interface ISourceOfStreamTypesState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  reason: any;
  data: any;
}

const initialState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  reason: null,
  data: null,
};

function reducer(
  state: ISourceOfStreamTypesState = initialState,
  action: ISourceOfStreamTypesAction
): ISourceOfStreamTypesState {
  switch (action.type) {
    case REQUEST:
      return {
        ...state,
        pending: true,
      };
    case SUCCESS:
      return {
        ...state,
        fulfilled: true,
        pending: false,
        rejected: false,
        reason: null,
        data: action.payload,
      };
    case FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: true,
        reason: action.payload,
      };
    default:
      return state;
  }
}

/**
 * Load customer data from the '/api/streaming/sources/types' endpoint.
 *
 * @see https://github.com/agraboso/redux-api-middleware
 */
function loadSourceOfStreamTypes({
  customerFilter,
}: {
  customerFilter?: number;
}): any {
  return (dispatch: Dispatch, getState: TGetRootState): any => {
    const { sourceOfStreamTypes } = getState();
    // data in sourceOfStreamTypes might be in 2 forms: 
    // 1) for different DSPs and 2) combined data for ALL DSPs

    // check the DSP streaming was selected and there is data for different DSPs in state:
    const isDspFilterApplied = customerFilter && sourceOfStreamTypes?.data?.[0].hasOwnProperty('customerId');
    // check that no DSP was selected and there is combined data for ALL DSPs in state:
    const showAllDSPsData = !customerFilter && sourceOfStreamTypes?.data?.[0].hasOwnProperty('displayName');
    const isSOSTypesDataUnchanged = isDspFilterApplied || showAllDSPsData;
    // when there is no DSP selected, but in state there is data for different DSPs:
    const isIncorectState = !customerFilter && sourceOfStreamTypes?.data?.[0].hasOwnProperty('customerId');
    const skipRequest = !isIncorectState && (sourceOfStreamTypes.fulfilled && isSOSTypesDataUnchanged) || sourceOfStreamTypes.pending;
    return dispatch({
      [RSAA]: {
        bailout: skipRequest,
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: async function payload(
              action: ISourceOfStreamTypesAction,
              state: ISourceOfStreamTypesState,
              res: any
            ): Promise<any> {
              const json = await res.json();

              return json;
            },
          },
          FAILURE,
        ],
        endpoint: getSourceOfStreamTypesURL(customerFilter),
        method: 'GET',
        credentials: 'same-origin',
      },
    });
  };
}

const selectSourceOfStreamTypes = (
  state: RootState,
  customerId?: number
): any[] =>
  customerId
    ? state.sourceOfStreamTypes?.data?.find(
        (data: any) => data.customerId === customerId
      )?.sourceOfStreamIds || []
    : // When there is no customerId, but store hasn't been updated yet.
    state.sourceOfStreamTypes?.data?.[0]?.sourceOfStreamIds
    ? []
    : state.sourceOfStreamTypes?.data;

export { loadSourceOfStreamTypes, selectSourceOfStreamTypes };

export default reducer;
